import { PortfolioDto } from "../../screens/portfolio-info/types/PortfolioDto";
import { PortfolioModel } from "../../screens/portfolio-info/types/PortfolioModel";
import { OrderDto } from "../../screens/portfolio-info/types/OrderDto";
import { OrderModel } from "../../screens/portfolio-info/types/OrderModel";
import { UnderlyingAssetDto } from "../../screens/portfolio-info/types/UnderlyingAssetDto";
import { UnderlyingAssetModel } from "../../screens/portfolio-info/types/UnderlyingAssetModel";
import { PortfolioSettingsDto } from "../../screens/portfolio-info/types/PortfolioSettingsDto";
import { PortfolioSettingsModel } from "../../screens/portfolio-info/types/PortfolioSettingsModel";
import { PortfolioBalanceHistoryEntryDto } from "../../screens/portfolio-info/types/PortfolioBalanceHistoryEntryDto";
import {
    PortfolioBalanceHistoryEntryModel
} from "../../screens/portfolio-info/types/PortfolioBalanceHistoryEntryModel";
import { assetService } from "../services/AssetService";
import {
    BlackScholesModelCalculationService,
    blackScholesModelCalculationService
} from "../services/BlackScholesModelCalculationService";
import { OptionInfoDto } from "../../screens/options-table/types/OptionInfoDto";
import { AssetType } from "../types/AssetType";
import { PositionDto } from "../../screens/portfolio-info/types/PositionDto";
import { PositionModel } from "../../screens/portfolio-info/types/PositionModel";
import { AutoOrderConfigurationDto } from "../../screens/portfolio-info/types/AutoOrderConfigurationDto";
import { AutoOrderConfigurationModel } from "../../screens/portfolio-info/types/AutoOrderConfigurationModel";
import { AutoOrderState } from "../../screens/portfolio-info/types/AutoOrderState";
import { PortfolioGroupDto } from "../../screens/portfolio-info/types/PortfolioGroupDto";
import { PortfolioGroupModel } from "../../screens/portfolio-info/types/PortfolioGroupModel";
import { OrderProcessingType } from "../types/OrderProcessingType";

class PortfolioMappers {
    map(dto: PortfolioDto, optionInfos: OptionInfoDto[], underlyingAssetPrice: number | null): PortfolioModel {
        return {
            id: dto.Id,
            name: dto.Name,
            description: dto.Description,
            marginBuffer: dto.MarginBuffer,
            inceptionDate: new Date(dto.InceptionDate),
            useValuationDate: dto.UseValuationDate,
            valuationDate: new Date(dto.ValuationDate),
            underlyingAsset: {
                id: dto.UnderlyingAsset.Id,
                name: dto.UnderlyingAsset.Name,
            },
            portfolioGroup: this.mapPortfolioGroup(dto.PortfolioGroup),
            positions: dto.Positions !== null
                ? dto.Positions.map(x => this.mapPosition(x, optionInfos, underlyingAssetPrice))
                : [],
            orders: dto.Orders !== null
                ? dto.Orders.map(x => this.mapOrder(x, optionInfos, underlyingAssetPrice))
                : [],
            portfolioBalanceHistory: dto.PortfolioBalanceHistory !== null
                ? dto.PortfolioBalanceHistory.map(this.mapPortfolioBalanceHistoryEntry)
                : [],
            portfolioSettings: dto.PortfolioSettings !== null
                ? this.mapSettings(dto.PortfolioSettings)
                : null,
            modifiedAt: null,
        };
    }

    mapModelToModel(model: PortfolioModel): PortfolioModel {
        return {
            id: model.id,
            name: model.name,
            description: model.description,
            marginBuffer: model.marginBuffer,
            inceptionDate: new Date(model.inceptionDate),
            useValuationDate: model.useValuationDate,
            valuationDate: new Date(model.valuationDate),
            underlyingAsset: {
                id: model.underlyingAsset.id,
                name: model.underlyingAsset.name,
            },
            portfolioGroup: this.mapPortfolioGroupModelToModel(model.portfolioGroup),
            positions: model.positions !== null
                ? model.positions.map(this.mapPositionModelToModel)
                : [],
            orders: model.orders !== null
                ? model.orders.map(this.mapOrderModelToModel)
                : [],
            portfolioBalanceHistory: model.portfolioBalanceHistory !== null
                ? model.portfolioBalanceHistory.map(this.mapPortfolioBalanceHistoryEntryModelToModel)
                : [],
            portfolioSettings: model.portfolioSettings !== null
                ? this.mapSettingsModelToModel(model.portfolioSettings)
                : null,
            modifiedAt: null,
        };
    }
    
    mapUnderlyingAsset(assetDto: UnderlyingAssetDto): UnderlyingAssetModel {
        return {
            id: assetDto.Id,
            name: assetDto.Name,
        }
    }

    mapPortfolioGroup(groupDto: PortfolioGroupDto): PortfolioGroupModel {
        return {
            id: groupDto.Id,
            name: groupDto.Name,
            underlyingAsset: this.mapUnderlyingAsset(groupDto.UnderlyingAsset),
            portfolioSettings: this.mapSettings(groupDto.PortfolioSettings),
        };
    }

    mapPortfolioGroupModelToModel(groupModel: PortfolioGroupModel): PortfolioGroupModel {
        return {
            id: groupModel.id,
            name: groupModel.name,
            underlyingAsset: {
                id: groupModel.underlyingAsset.id,
                name: groupModel.underlyingAsset.name,
            },
            portfolioSettings: groupModel.portfolioSettings !== null
                ? this.mapSettingsModelToModel(groupModel.portfolioSettings)
                : null,
        };
    }

    mapAutoOrderConfiguration(dto: AutoOrderConfigurationDto | null): AutoOrderConfigurationModel | null {
        if (dto === null) {
            return null;
        }

        return {
            id: dto.Id,
            orderId: dto.OrderId,
            strategy: dto.Strategy,
            state: dto.State ?? AutoOrderState.NotTriggered,
            stateDescription: dto.StateDescription ?? null,
            isActive: dto.IsActive,
            targetPrice: dto.TargetPrice,
            maxImpliedVolatilityLevel: dto.MaxImpliedVolatilityLevel,
            maxPriceDelta: dto.MaxPriceDelta,
            netDelta: dto.NetDelta,
        };
    }

    mapAutoOrderConfigurationToDto(model: AutoOrderConfigurationModel | null): AutoOrderConfigurationDto | null {
        if (model === null || model === undefined) {
            return null;
        }

        return {
            Id: model.id,
            OrderId: model.orderId,
            Strategy: model.strategy,
            IsActive: model.isActive,
            TargetPrice: model.targetPrice,
            MaxImpliedVolatilityLevel: model.maxImpliedVolatilityLevel,
            MaxPriceDelta: model.maxPriceDelta,
            NetDelta: model.netDelta,
        };
    }

    mapPositionModelToModel(positionDto: PositionModel): PositionModel {
        return {
            id: positionDto.id,
            portfolioId: positionDto.portfolioId,
            type: positionDto.type,
            symbol: positionDto.symbol,
            targetPlatform: positionDto.targetPlatform,
            expireAt: positionDto.expireAt,
            strike: positionDto.strike,
            size: positionDto.size,
            fee: positionDto.fee ?? 0,
            volume: positionDto.volume,
            averageOpenPrice: positionDto.averageOpenPrice,
            expirationPrice: positionDto.expirationPrice,
            impliedVolatility: positionDto.impliedVolatility,
            customImpliedVolatility: positionDto.customImpliedVolatility,
            customImpliedVolatilityStep: positionDto.customImpliedVolatilityStep,
            markPrice: positionDto.markPrice,
            delta: positionDto.delta,
            gamma: positionDto.gamma,
            vega: positionDto.vega,
            theta: positionDto.theta,
        };
    }
    
    mapOrderModelToModel(orderModel: OrderModel): OrderModel {
        return new OrderModel({
            externalId: orderModel.externalId,
            exchangeOrderId: orderModel.exchangeOrderId,
            internalId: crypto.randomUUID(),
            autoOrderConfiguration: orderModel.autoOrderConfiguration,
            state: orderModel.state,
            targetPlatform: orderModel.targetPlatform,
            openedPositionAt: orderModel.openedPositionAt,
            expireAt: orderModel.expireAt,
            impliedVolatilityAtOpen: orderModel.impliedVolatilityAtOpen,
            customImpliedVolatility: orderModel.customImpliedVolatility,
            customImpliedVolatilityStep: orderModel.customImpliedVolatilityStep,
            type: orderModel.type,
            strike: orderModel.strike,
            symbol: orderModel.symbol,
            size: orderModel.size,
            sizeStep: orderModel.sizeStep,
            fee: orderModel.fee ?? 0,
            processingType: orderModel.processingType,
            openPrice: orderModel.openPrice,
            markPrice: orderModel.markPrice,
            markImpliedVolatility: orderModel.markImpliedVolatility,
            underlyingPriceAtOpen: orderModel.underlyingPriceAtOpen,
            delta: orderModel.delta,
            gamma: orderModel.gamma,
            vega: orderModel.vega,
            theta: orderModel.theta,
            comment: orderModel.comment,
            expirationPrice: orderModel.expirationPrice,
            dateForPricing: orderModel.dateForPricing,
            impliedVolatilityForPricing: orderModel.impliedVolatilityForPricing,
            underlyingPriceForPricing: orderModel.underlyingPriceForPricing,
        });
    }

    mapPortfolioBalanceHistoryEntry(itemDto: PortfolioBalanceHistoryEntryDto): PortfolioBalanceHistoryEntryModel {
        return {
            createdAt: itemDto.CreatedAt,
            currentBalance: itemDto.CurrentBalance,
            expirationBalance: itemDto.ExpirationBalance,
        };
    }

    mapPortfolioBalanceHistoryEntryModelToModel(itemModel: PortfolioBalanceHistoryEntryModel): PortfolioBalanceHistoryEntryModel {
        return {
            createdAt: itemModel.createdAt,
            currentBalance: itemModel.currentBalance,
            expirationBalance: itemModel.expirationBalance,
        };
    }
    
    getAssetParameters(
        assetType: AssetType,
        strike: number | null,
        expireAt: Date | null,
        impliedVolatility: number | null,
        riskFreeInterest: number | null,
        underlyingAssetPrice: number | null,
        currentDateTime: Date = new Date(),
    ): {
        price: number,
        delta: number,
        gamma: number | null,
        vega: number | null,
        theta: number | null,
        currentDateTime?: Date | null,
    } {
        let markPrice: number = 0;
        let delta: number = 0;
        let gamma: number | null = null;
        let vega: number | null = null;
        let theta: number | null = null;
    
        const isOption = assetService.isOptionType(assetType);
    
        if (!isOption) {
            markPrice = underlyingAssetPrice ?? 0;
            delta = 1;
        } else if (isOption && impliedVolatility && strike && expireAt && underlyingAssetPrice) {
            const timeToExpirationInShareOfYear = blackScholesModelCalculationService
                .getTimeToExpirationInShareOfYear(expireAt, currentDateTime);
        
            const riskFreeInterestValue = riskFreeInterest ?? BlackScholesModelCalculationService.riskFreeRate;
            
            markPrice = blackScholesModelCalculationService.getPremium(
                assetType, underlyingAssetPrice, strike, timeToExpirationInShareOfYear,
                impliedVolatility, riskFreeInterestValue);
        
            delta = blackScholesModelCalculationService.getDelta(
                assetType, underlyingAssetPrice, strike, timeToExpirationInShareOfYear,
                impliedVolatility, riskFreeInterestValue);
        
            gamma = blackScholesModelCalculationService.getGamma(
                underlyingAssetPrice, strike, timeToExpirationInShareOfYear, 
                impliedVolatility, riskFreeInterestValue);
        
            vega = blackScholesModelCalculationService.getVega(
                underlyingAssetPrice, strike, timeToExpirationInShareOfYear,
                impliedVolatility, riskFreeInterestValue);
        
            theta = blackScholesModelCalculationService.getTheta(
                assetType, underlyingAssetPrice, strike, timeToExpirationInShareOfYear,
                impliedVolatility, riskFreeInterestValue);
        }
    
        return { price: markPrice, delta, gamma, vega, theta };
    }

    mapPosition(
        positionDto: PositionDto,
        optionInfos: OptionInfoDto[],
        underlyingAssetPrice: number | null
    ): PositionModel {
        const optionInfoDto = optionInfos.find(x => x.Symbol === positionDto.Symbol) ?? null;
        const impliedVolatility = optionInfoDto?.MarkImpliedVolatility ?? null;
        const riskFreeInterest = optionInfoDto?.RiskFreeInterest ?? BlackScholesModelCalculationService.riskFreeRate;

        const { price, delta, gamma, vega, theta } =
            this.getAssetParameters(positionDto.Type, positionDto.Strike, positionDto.ExpireAt,
                impliedVolatility, riskFreeInterest, underlyingAssetPrice);

        return {
            id: positionDto.Id,
            portfolioId: positionDto.PortfolioId,
            type: positionDto.Type,
            symbol: positionDto.Symbol,
            targetPlatform: positionDto.TargetPlatform,
            expireAt: positionDto.ExpireAt,
            strike: positionDto.Strike,
            size: positionDto.Size,
            fee: positionDto.Fee ?? 0,
            volume: positionDto.Volume,
            averageOpenPrice: positionDto.AverageOpenPrice,
            expirationPrice: positionDto.ExpirationPrice,
            impliedVolatility: impliedVolatility,
            customImpliedVolatility: positionDto.CustomImpliedVolatility,
            customImpliedVolatilityStep: positionDto.CustomImpliedVolatilityStep,
            markPrice: price,
            delta: delta * positionDto.Size,
            gamma: (gamma ?? 0) * positionDto.Size,
            vega: (vega ?? 0) * positionDto.Size,
            theta: (theta ?? 0) * positionDto.Size,
        };
    }
    
    mapOrder(
        orderDto: OrderDto,
        optionInfos: OptionInfoDto[],
        underlyingAssetPrice: number | null
    ): OrderModel {
        const optionInfoDto = optionInfos.find(x => x.Symbol === orderDto.Symbol) ?? null;
        const impliedVolatility = optionInfoDto?.MarkImpliedVolatility ?? null;
        const riskFreeInterest = optionInfoDto?.RiskFreeInterest ?? BlackScholesModelCalculationService.riskFreeRate;
        
        const { price, delta, gamma, vega, theta } =
            this.getAssetParameters(orderDto.Type, orderDto.Strike, orderDto.ExpireAt,
                impliedVolatility, riskFreeInterest, underlyingAssetPrice);
        
        return new OrderModel({
            externalId: orderDto.Id,
            portfolioId: orderDto.PortfolioId ?? null,
            exchangeOrderId: orderDto.ExchangeOrderId,
            internalId: crypto.randomUUID(),
            autoOrderConfiguration: this.mapAutoOrderConfiguration(orderDto.AutoOrderConfiguration),
            state: orderDto.State,
            targetPlatform: orderDto.TargetPlatform,
            openedPositionAt: orderDto.OpenedPositionAt,
            expireAt: orderDto.ExpireAt,
            impliedVolatilityAtOpen: orderDto.ImpliedVolatilityAtOpen,
            customImpliedVolatility: orderDto.CustomImpliedVolatility,
            customImpliedVolatilityStep: orderDto.CustomImpliedVolatilityStep ?? null,
            type: orderDto.Type,
            strike: orderDto.Strike,
            symbol: orderDto.Symbol,
            size: orderDto.Size,
            sizeStep: orderDto.SizeStep,
            fee: orderDto.Fee ?? 0,
            processingType: orderDto.ProcessingType ?? OrderProcessingType.Taker,
            openPrice: orderDto.OpenPrice,
            markPrice: price,
            markImpliedVolatility: impliedVolatility,
            underlyingPriceAtOpen: orderDto.UnderlyingPriceAtOpen,
            delta: delta * orderDto.Size,
            gamma: (gamma ?? 0) * orderDto.Size,
            vega: (vega ?? 0) * orderDto.Size,
            theta: (theta ?? 0) * orderDto.Size,
            comment: orderDto.Comment,
            expirationPrice: orderDto.ExpirationPrice,
            dateForPricing: orderDto.DateForPricing,
            impliedVolatilityForPricing: orderDto.ImpliedVolatilityForPricing,
            underlyingPriceForPricing: orderDto.UnderlyingPriceForPricing,
        });
    }

    mapSettings(settingsDto: PortfolioSettingsDto | null): PortfolioSettingsModel | null {
        if (settingsDto === null) {
            return null;
        }
        
        return {
            useOffset: settingsDto.UseOffset,
            offset: settingsDto.Offset,
            minUnderlyingPrice: settingsDto.MinUnderlyingPrice,
            maxUnderlyingPrice: settingsDto.MaxUnderlyingPrice,
            useBalanceRangeRestriction: settingsDto.UseBalanceRangeRestriction,
            minBalance: settingsDto.MinBalance,
            maxBalance: settingsDto.MaxBalance,
            calculationStep: settingsDto.CalculationStep,
            selectedOptionParameterType: settingsDto.SelectedOptionParameterType,
            selectedTableInfoType: settingsDto.SelectedTableInfoType,
            selectedProfileTypes: settingsDto.SelectedProfileTypes
                ? [...settingsDto.SelectedProfileTypes]
                : [],
            selectedCustomDate: settingsDto.SelectedCustomDate,
            hideCommitedBalance: settingsDto.HideCommitedBalance,
            commitedBalance: settingsDto.CommitedBalance,
            showTradeSessionBalanceLine: settingsDto.ShowTradeSessionBalanceLine,
            tradeSessionBalance: settingsDto.TradeSessionBalance,
            useSessionBalanceAsZero: settingsDto.UseSessionBalanceAsZero,
            showSupportLine: settingsDto.ShowSupportLine,
            supportLinePrice: settingsDto.SupportLinePrice,
            showResistanceLine: settingsDto.ShowResistanceLine,
            resistanceLinePrice: settingsDto.ResistanceLinePrice,
            useCustomPrice: settingsDto.UseCustomPrice,
            customPrice: settingsDto.CustomPrice,
            customPriceStep: settingsDto.CustomPriceStep,
            usePopupForStepChangeSelector: settingsDto.UsePopupForStepChangeSelector,
            useComparedPortfolio: settingsDto.UseComparedPortfolio,
            comparedPortfolioId: settingsDto.ComparedPortfolioId,
            showDeltaNeutralPriceLines: settingsDto.ShowDeltaNeutralPriceLines,
            showPortfolioPerformance: settingsDto.ShowPortfolioPerformance,
            usePortfolioAnnualisedRoi: settingsDto.UsePortfolioAnnualisedRoi,
            showDeltaValues: settingsDto.ShowDeltaValues,
            balanceStartValueX: settingsDto.BalanceStartValueX,
            balanceEndValueX: settingsDto.BalanceEndValueX,
            balanceStartValueY: settingsDto.BalanceStartValueY,
            balanceEndValueY: settingsDto.BalanceEndValueY,
            isTripleCustomBalanceEnabled: settingsDto.IsTripleCustomBalanceEnabled,
            isTripleCustomBalanceUsingEqualShifts: settingsDto.IsTripleCustomBalanceUsingEqualShifts,
            isCustomImpliedVolatilityEnabled: settingsDto.IsCustomImpliedVolatilityEnabled,
            positionStateFilter: settingsDto.PositionStateFilter,
            positionsAssetTypeFilter: settingsDto.PositionsAssetTypeFilter,
            positionsTargetPlatformFilter: settingsDto.PositionsTargetPlatformFilter,
            includeFeesInBalance: settingsDto.IncludeFeesInBalance ?? false,
            mergePositionsAcrossPlatforms: settingsDto.MergePositionsAcrossPlatforms,
            orderStateFilter: settingsDto.OrderStateFilter,
            ordersAssetTypeFilter: settingsDto.OrdersAssetTypeFilter,
            ordersTargetPlatformFilter: settingsDto.OrdersTargetPlatformFilter,
        }
    }

    mapSettingsModelToModel(settingsModel: PortfolioSettingsModel): PortfolioSettingsModel {
        return {
            useOffset: settingsModel.useOffset,
            offset: settingsModel.offset,
            minUnderlyingPrice: settingsModel.minUnderlyingPrice,
            maxUnderlyingPrice: settingsModel.maxUnderlyingPrice,
            calculationStep: settingsModel.calculationStep,
            useBalanceRangeRestriction: settingsModel.useBalanceRangeRestriction,
            minBalance: settingsModel.minBalance,
            maxBalance: settingsModel.maxBalance,
            selectedOptionParameterType: settingsModel.selectedOptionParameterType,
            selectedTableInfoType: settingsModel.selectedTableInfoType,
            selectedProfileTypes: [...settingsModel.selectedProfileTypes],
            selectedCustomDate: settingsModel.selectedCustomDate,
            hideCommitedBalance: settingsModel.hideCommitedBalance,
            commitedBalance: settingsModel.commitedBalance,
            showTradeSessionBalanceLine: settingsModel.showTradeSessionBalanceLine,
            tradeSessionBalance: settingsModel.tradeSessionBalance,
            useSessionBalanceAsZero: settingsModel.useSessionBalanceAsZero,
            showSupportLine: settingsModel.showSupportLine,
            supportLinePrice: settingsModel.supportLinePrice,
            showResistanceLine: settingsModel.showResistanceLine,
            resistanceLinePrice: settingsModel.resistanceLinePrice,
            useCustomPrice: settingsModel.useCustomPrice,
            customPrice: settingsModel.customPrice,
            customPriceStep: settingsModel.customPriceStep,
            usePopupForStepChangeSelector: settingsModel.usePopupForStepChangeSelector,
            useComparedPortfolio: settingsModel.useComparedPortfolio,
            comparedPortfolioId: settingsModel.comparedPortfolioId,
            showDeltaNeutralPriceLines: settingsModel.showDeltaNeutralPriceLines,
            showPortfolioPerformance: settingsModel.showPortfolioPerformance,
            usePortfolioAnnualisedRoi: settingsModel.usePortfolioAnnualisedRoi,
            showDeltaValues: settingsModel.showDeltaValues,
            balanceStartValueX: settingsModel.balanceStartValueX,
            balanceEndValueX: settingsModel.balanceEndValueX,
            balanceStartValueY: settingsModel.balanceStartValueY,
            balanceEndValueY: settingsModel.balanceEndValueY,
            isTripleCustomBalanceEnabled: settingsModel.isTripleCustomBalanceEnabled,
            isTripleCustomBalanceUsingEqualShifts: settingsModel.isTripleCustomBalanceUsingEqualShifts,
            isCustomImpliedVolatilityEnabled: settingsModel.isCustomImpliedVolatilityEnabled,
            positionStateFilter: settingsModel.positionStateFilter,
            positionsAssetTypeFilter: settingsModel.positionsAssetTypeFilter,
            positionsTargetPlatformFilter: settingsModel.positionsTargetPlatformFilter,
            includeFeesInBalance: settingsModel.includeFeesInBalance ?? false,
            mergePositionsAcrossPlatforms: settingsModel.mergePositionsAcrossPlatforms,
            orderStateFilter: settingsModel.orderStateFilter,
            ordersAssetTypeFilter: settingsModel.ordersAssetTypeFilter,
            ordersTargetPlatformFilter: settingsModel.ordersTargetPlatformFilter,
        }
    }

    mapSettingsModelToDto(settingsModel: PortfolioSettingsModel | null): PortfolioSettingsDto | null {
        return settingsModel !== null
            ?   {
                    UseOffset: settingsModel.useOffset,
                    Offset: settingsModel.offset,
                    MinUnderlyingPrice: settingsModel.minUnderlyingPrice,
                    MaxUnderlyingPrice: settingsModel.maxUnderlyingPrice,
                    CalculationStep: settingsModel.calculationStep,
                    UseBalanceRangeRestriction: settingsModel.useBalanceRangeRestriction,
                    MinBalance: settingsModel.minBalance,
                    MaxBalance: settingsModel.maxBalance,
                    SelectedOptionParameterType: settingsModel.selectedOptionParameterType,
                    SelectedTableInfoType: settingsModel.selectedTableInfoType,
                    SelectedProfileTypes: [...settingsModel.selectedProfileTypes],
                    SelectedCustomDate: settingsModel.selectedCustomDate,
                    HideCommitedBalance: settingsModel.hideCommitedBalance,
                    CommitedBalance: settingsModel.commitedBalance,
                    ShowTradeSessionBalanceLine: settingsModel.showTradeSessionBalanceLine,
                    TradeSessionBalance: settingsModel.tradeSessionBalance,
                    UseSessionBalanceAsZero: settingsModel.useSessionBalanceAsZero,
                    ShowSupportLine: settingsModel.showSupportLine,
                    SupportLinePrice: settingsModel.supportLinePrice,
                    ShowResistanceLine: settingsModel.showResistanceLine,
                    ResistanceLinePrice: settingsModel.resistanceLinePrice,
                    UseCustomPrice: settingsModel.useCustomPrice,
                    CustomPrice: settingsModel.customPrice,
                    CustomPriceStep: settingsModel.customPriceStep,
                    UsePopupForStepChangeSelector: settingsModel.usePopupForStepChangeSelector,
                    UseComparedPortfolio: settingsModel.useComparedPortfolio,
                    ComparedPortfolioId: settingsModel.comparedPortfolioId,
                    ShowDeltaNeutralPriceLines: settingsModel.showDeltaNeutralPriceLines,
                    ShowPortfolioPerformance: settingsModel.showPortfolioPerformance,
                    UsePortfolioAnnualisedRoi: settingsModel.usePortfolioAnnualisedRoi,
                    ShowDeltaValues: settingsModel.showDeltaValues,
                    BalanceStartValueX: settingsModel.balanceStartValueX,
                    BalanceEndValueX: settingsModel.balanceEndValueX,
                    BalanceStartValueY: settingsModel.balanceStartValueY,
                    BalanceEndValueY: settingsModel.balanceEndValueY,
                    IsTripleCustomBalanceEnabled: settingsModel.isTripleCustomBalanceEnabled,
                    IsTripleCustomBalanceUsingEqualShifts: settingsModel.isTripleCustomBalanceUsingEqualShifts,
                    IsCustomImpliedVolatilityEnabled: settingsModel.isCustomImpliedVolatilityEnabled,
                    PositionStateFilter: settingsModel.positionStateFilter,
                    PositionsAssetTypeFilter: settingsModel.positionsAssetTypeFilter,
                    PositionsTargetPlatformFilter: settingsModel.positionsTargetPlatformFilter,
                    IncludeFeesInBalance: settingsModel.includeFeesInBalance ?? false,
                    MergePositionsAcrossPlatforms: settingsModel.mergePositionsAcrossPlatforms,
                    OrderStateFilter: settingsModel.orderStateFilter,
                    OrdersAssetTypeFilter: settingsModel.ordersAssetTypeFilter,
                    OrdersTargetPlatformFilter: settingsModel.ordersTargetPlatformFilter,
                }
            :   null;
    }
    
    mapToDto(model: PortfolioModel): PortfolioDto {
        return {
            Id: model.id,
            Name: model.name,
            Description: model.description,
            MarginBuffer: model.marginBuffer,
            InceptionDate: model.inceptionDate,
            UseValuationDate: model.useValuationDate,
            ValuationDate: model.valuationDate,
            UnderlyingAsset: {
                Id: model.underlyingAsset.id,
                Name: model.underlyingAsset.name,
            },
            PortfolioGroup: {
                Id: model.portfolioGroup.id,
                Name: model.portfolioGroup.name,
                UnderlyingAsset: {
                    Id: model.portfolioGroup.underlyingAsset.id,
                    Name: model.portfolioGroup.underlyingAsset.name,
                },
                PortfolioSettings: this.mapSettingsModelToDto(model.portfolioGroup.portfolioSettings),
            },
            Positions: [],
            Orders: model.orders !== null
                ? model.orders.map(x => this.mapOrderToDto(x))
                : [],
            PortfolioBalanceHistory: [],
            PortfolioSettings: this.mapSettingsModelToDto(model.portfolioSettings),
            CurrentBalance: 0,
            ExpirationBalance: 0,
        };
    }

    mapOrderToDto(orderModel: OrderModel): OrderDto {
        return {
            Id: orderModel.externalId,
            ExchangeOrderId: orderModel.exchangeOrderId,
            AutoOrderConfiguration: this.mapAutoOrderConfigurationToDto(orderModel.autoOrderConfiguration),
            State: orderModel.state,
            TargetPlatform: orderModel.targetPlatform,
            OpenedPositionAt: orderModel.openedPositionAt,
            ExpireAt: orderModel.expireAt !== null
                ? orderModel.expireAt
                : null,
            ImpliedVolatilityAtOpen: orderModel.impliedVolatilityAtOpen,
            CustomImpliedVolatility: orderModel.customImpliedVolatility,
            Type: orderModel.type,
            Strike: orderModel.strike,
            Symbol: orderModel.symbol,
            Size: orderModel.size,
            SizeStep: orderModel.sizeStep,
            Fee: orderModel.fee ?? 0,
            ProcessingType: orderModel.processingType,
            OpenPrice: orderModel.openPrice,
            MarkPrice: orderModel.markPrice,
            MarkImpliedVolatility: orderModel.markImpliedVolatility,
            UnderlyingPriceAtOpen: orderModel.underlyingPriceAtOpen,
            Delta: orderModel.delta,
            Gamma: orderModel.gamma,
            Vega: orderModel.vega,
            Theta: orderModel.theta,
            Comment: orderModel.comment,
            ExpirationPrice: orderModel.expirationPrice,
            DateForPricing: orderModel.dateForPricing,
            CustomImpliedVolatilityStep: orderModel.customImpliedVolatilityStep,
            ImpliedVolatilityForPricing: orderModel.impliedVolatilityForPricing,
            UnderlyingPriceForPricing: orderModel.underlyingPriceForPricing,
        };
    }
}

export const portfolioMappers = new PortfolioMappers();
